import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AlertTriangle, User } from "lucide-react";
import { Link } from "react-router-dom";
import { useContext } from "react";
import GlobalContext from "../context/GlobalContext";
import Navbar from "../components/shared/Navbar";

const MedicalLicenseForm = () => {
  const [licenseNumber, setLicenseNumber] = useState("");
  const [isValid, setIsValid] = useState(null);
  const { userInfo } = useContext(GlobalContext);
  const [isLoading, setIsLoading] = useState(false);

  const handleProceed = async () => {
    setIsLoading(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const isValidLicense =
      licenseNumber.length >= 8 && /^\d+$/.test(licenseNumber);
    setIsValid(isValidLicense);
    setIsLoading(false);
  };

  const handleGoBack = () => {
    setLicenseNumber("");
    setIsValid(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8 md:py-16">
        <div className="w-full max-w-2xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-2xl md:text-3xl lg:text-4xl text-gray-700 leading-relaxed">
              Kindly proceed by entering your{" "}
              <span className="text-green-600 font-semibold">
                license number below.
              </span>
            </h1>
          </div>

          {/* Form Container */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 lg:p-12 relative">
            {/* User Icon */}
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
              <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
            </div>

            <div className="pt-8 space-y-6">
              {/* Input Field */}
              <div className="space-y-2">
                <label
                  htmlFor="license"
                  className="block text-lg font-medium text-gray-700 text-center md:text-left"
                >
                  Medical License Number
                </label>
                <Input
                  id="license"
                  type="text"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  placeholder="123456790"
                  className={`h-14 text-lg rounded-xl border-2 transition-colors ${
                    isValid === false
                      ? "border-red-400 bg-red-50"
                      : isValid === true
                      ? "border-green-400 bg-green-50"
                      : "border-gray-200 focus:border-green-400"
                  }`}
                />
              </div>

              {/* Error Message */}
              {isValid === false && (
                <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium">
                    Medical License Number Not Found, Please Try Again
                  </span>
                </div>
              )}

              {/* Success Message */}
              {isValid === true && (
                <div className="text-center text-green-600 font-medium">
                  License verified successfully!
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  onClick={handleGoBack}
                  variant="outline"
                  className="flex-1 h-12 text-lg border-green-600 text-green-600 hover:bg-green-50 rounded-xl"
                >
                  Go Back
                </Button>
                <Button
                  onClick={handleProceed}
                  disabled={!licenseNumber.trim() || isLoading}
                  className="flex-1 h-12 text-lg bg-green-600 hover:bg-green-700 rounded-xl"
                >
                  {isLoading ? "Processing..." : "Proceed"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MedicalLicenseForm;
